import { Box, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import type {
  InvestorListDTO,
  InvoiceTransactionDraftDTO,
  PaymentMethod,
  TransactionType,
} from '@inversor-hub/types';
import EurCurrencyField from '@/components/form/EurCurrencyField';
import { expenseCategories, incomeCategories, paymentMethodLabels } from '../constants';
import { TransactionCategorySelect } from '../TransactionCategorySelect';
import type { InvoiceDraftReviewController } from './useInvoiceDraftReview';
import { Field, FieldHead } from './shared';

interface ReviewDraftFormProps {
  draft: InvoiceTransactionDraftDTO;
  investments?: InvestorListDTO[];
  review: InvoiceDraftReviewController;
}

export const ReviewDraftForm = ({ draft, investments, review }: ReviewDraftFormProps) => {
  const { form, edited, conf } = review.state;
  const { setField, handleAmountChange } = review.actions;

  return (
    <Stack spacing={1.9}>
      <Field label="Tipo" conf="high" edited={edited.type}>
        <Box sx={{ display: 'flex', p: 0.5, gap: 0.5 }}>
          {(['EXPENSE', 'INCOME'] as TransactionType[]).map((value) => {
            const selected = form.type === value;
            const income = value === 'INCOME';

            return (
              <Box
                key={value}
                component="button"
                type="button"
                onClick={() => {
                  setField('type', value);
                  const nextOptions = income ? incomeCategories : expenseCategories;
                  if (!nextOptions.includes(form.category)) {
                    setField('category', nextOptions[0]);
                  }
                }}
                sx={{
                  flex: 1,
                  py: 0.9,
                  borderRadius: '7px',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontWeight: 600,
                  fontSize: 13.5,
                  bgcolor: selected ? (income ? 'success.50' : 'primary.50') : 'transparent',
                  color: selected ? (income ? 'success.main' : 'primary.dark') : 'text.secondary',
                }}
              >
                {income ? 'Ingreso' : 'Gasto'}
              </Box>
            );
          })}
        </Box>
      </Field>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.75 }}>
        <Box>
          <FieldHead label="Importe" conf={conf.amount} edited={edited.amount} />
          <EurCurrencyField
            size="small"
            value={form.amount ? Number(form.amount) : null}
            onChange={handleAmountChange}
            error={conf.amount === 'low' && !edited.amount}
          />
        </Box>
        <Box>
          <FieldHead label="Fecha" conf={conf.date} edited={edited.date} />
          <TextField
            size="small"
            fullWidth
            type="date"
            value={form.date}
            onChange={(event) => setField('date', event.target.value)}
            error={conf.date === 'low' && !edited.date}
          />
        </Box>
      </Box>

      <Box>
        <FieldHead label="Categoría" conf={conf.category} edited={edited.category} />
        <TransactionCategorySelect
          size="small"
          hideLabel
          value={form.category}
          type={form.type}
          scope="matching-type"
          error={conf.category === 'low' && !edited.category}
          onChange={(category) => category && setField('category', category)}
        />
      </Box>

      <Box>
        <FieldHead label="Inversión asignada" conf={conf.investment} edited={edited.investmentId} />
        <Select
          size="small"
          fullWidth
          displayEmpty
          value={form.investmentId}
          error={conf.investment === 'low' && !edited.investmentId}
          onChange={(event) => setField('investmentId', event.target.value)}
          renderValue={(value) => {
            if (!value) {
              return (
                <Box component="span" sx={{ color: 'grey.300' }}>
                  Selecciona una inversión
                </Box>
              );
            }
            const investment = investments?.find((item) => item.id === value);
            return investment ? review.meta.investmentLabel(investment) : value;
          }}
        >
          {investments?.map((investment) => (
            <MenuItem key={investment.id} value={investment.id}>
              {review.meta.investmentLabel(investment)}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box>
        <FieldHead label="Método de pago" conf="med" edited={edited.paymentMethod} />
        <Select
          size="small"
          fullWidth
          displayEmpty
          value={form.paymentMethod}
          onChange={(event) => setField('paymentMethod', event.target.value as PaymentMethod | '')}
          renderValue={(value) =>
            value ? (
              paymentMethodLabels[value as PaymentMethod]
            ) : (
              <Box component="span" sx={{ color: 'grey.300' }}>
                Sin método
              </Box>
            )
          }
        >
          <MenuItem value="">Sin método</MenuItem>
          {Object.entries(paymentMethodLabels).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box>
        <FieldHead label="Concepto" />
        <TextField
          size="small"
          fullWidth
          placeholder="Concepto de la transacción"
          value={form.concept}
          onChange={(event) => setField('concept', event.target.value)}
        />
      </Box>

      <Box>
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>
          Emisor
        </Typography>
        <Box
          sx={{
            borderRadius: '10px',
            border: 1.5,
            borderColor: 'grey.200',
            bgcolor: 'grey.50',
            px: 1.6,
            py: 1.25,
          }}
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'text.primary' }}>
            {draft.supplierName || 'Emisor desconocido'}
          </Typography>
          {draft.invoiceNumber && (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>
              Factura n.º {draft.invoiceNumber}
            </Typography>
          )}
        </Box>
      </Box>
    </Stack>
  );
};
